import { useForm, STEP_TITLES } from "../../context/FormContext";
import { Check, Lock } from "lucide-react";

export default function ProgressSidebar() {
  const { step, setStep, restrictedSteps } = useForm();
  // Build a set of step indices that are navigable in "restricted-edit" mode.
  // In normal mode every step is navigable (through the usual isDone / idx<step rules).
  const isRestricted = Array.isArray(restrictedSteps) && restrictedSteps.length > 0;
  const allowedSet = isRestricted ? new Set(restrictedSteps.map((n) => Number(n))) : null;

  return (
    <aside
      className="hidden lg:block bg-slate-900 text-white lg:fixed lg:left-0 lg:top-0 lg:bottom-0 lg:w-[28rem] lg:overflow-y-auto"
      data-testid="progress-sidebar"
    >
      <div className="p-8 md:p-10 flex flex-col h-full">
        <div className="mb-10">
          <img
            src="/logo-unioncasa-white.png"
            alt="UnionCasa - Associazione Nazionale a tutela della proprietà immobiliare"
            className="w-64 mb-6 select-none"
            data-testid="sidebar-logo"
          />
          <p
            className="text-sm md:text-base font-light text-slate-300 tracking-wide whitespace-nowrap"
            data-testid="sidebar-subtitle"
          >
            Attestazione per Canone Concordato
          </p>
          <h1
            className="font-serif text-5xl md:text-6xl lg:text-7xl font-light leading-none tracking-tight text-white mt-2"
            data-testid="sidebar-title"
          >
            Milano
          </h1>
          <p className="text-xs text-slate-400 mt-5 leading-relaxed">
            Accordo locale per la città di Milano.
            <br />
            Depositato il 12/06/2023.
          </p>
        </div>

        <nav className="space-y-1" data-testid="step-nav">
          {STEP_TITLES.map((title, i) => {
            const idx = i + 1;
            const isActive = idx === step;
            const isDone = idx < step;
            // In restricted-edit mode a step is clickable only if it's in the
            // admin's allowed set. All others are locked and shown with 🔒.
            const isLockedByRestriction = isRestricted && !allowedSet.has(idx);
            const canClick = isLockedByRestriction
              ? false
              : idx < step || (isRestricted && allowedSet.has(idx));
            return (
              <button
                key={idx}
                type="button"
                onClick={() => canClick && setStep(idx)}
                disabled={!canClick}
                title={isLockedByRestriction ? "Step bloccato — l'amministrazione non ha autorizzato modifiche a questa sezione" : undefined}
                className={`w-full flex items-center gap-4 px-3 py-2.5 rounded text-left transition-colors ${
                  isLockedByRestriction
                    ? "text-slate-500 opacity-60 cursor-not-allowed"
                    : isActive
                    ? "bg-white/10 text-white"
                    : isDone
                    ? "text-slate-300 hover:bg-white/5"
                    : "text-slate-500"
                }`}
                data-testid={`step-nav-${idx}`}
              >
                <span
                  className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium shrink-0 ${
                    isLockedByRestriction
                      ? "bg-white/5 text-slate-500"
                      : isDone
                      ? "bg-green-800 text-white"
                      : isActive
                      ? "bg-white text-slate-900"
                      : "bg-white/10 text-slate-400"
                  }`}
                >
                  {isLockedByRestriction ? <Lock size={12} /> : isDone ? <Check size={14} /> : idx}
                </span>
                <span className={`text-sm ${isActive ? "font-medium" : ""}`}>{title}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}
